import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGrantConsentMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import type { ConsentBasis } from "@/model/interface/ConsentEvent";

const ALL_PHONES = "-";

const grantConsentSchema = z
  .object({
    consentType: z.string().min(1),
    source: z.string().min(1),
    consentBasis: z.enum(["EXPRESS", "WRITTEN"]),
    phoneNumberE164: z.string().optional(),
    evidenceUrl: z.string().optional(),
    notes: z.string().optional(),
  })
  .refine(
    (values) => values.consentBasis !== "WRITTEN" || values.source === "WEB_FORM" || !!values.evidenceUrl?.trim(),
    {
      message: "Written consent needs a link to the signed form or the record that shows it",
      path: ["evidenceUrl"],
    },
  );

type GrantConsentFormValues = z.infer<typeof grantConsentSchema>;

const DEFAULT_VALUES: GrantConsentFormValues = {
  consentType: "CALL_AND_SMS",
  source: "MANUAL",
  consentBasis: "EXPRESS",
  phoneNumberE164: ALL_PHONES,
  evidenceUrl: "",
  notes: "",
};

function basisImpliedBySource(source: string): ConsentBasis | null {
  if (source === "WEB_FORM") {
    return "WRITTEN";
  }
  if (source === "VERBAL") {
    return "EXPRESS";
  }
  return null;
}

interface GrantConsentDialogProps {
  contactId: string;
  phones?: { phoneNumberE164?: string; phoneNumber?: string }[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GrantConsentDialog: React.FC<GrantConsentDialogProps> = ({ contactId, phones, open, onOpenChange }) => {
  const [grantConsent, { isLoading }] = useGrantConsentMutation();

  const form = useForm<GrantConsentFormValues>({
    resolver: zodResolver(grantConsentSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const source = form.watch("source");
  const consentBasis = form.watch("consentBasis");
  const impliedBasis = basisImpliedBySource(source);

  useEffect(() => {
    if (open) {
      form.reset(DEFAULT_VALUES);
    }
  }, [open]);

  useEffect(() => {
    if (impliedBasis && impliedBasis !== consentBasis) {
      form.setValue("consentBasis", impliedBasis);
    }
  }, [impliedBasis, consentBasis, form]);

  const onSubmit = async (values: GrantConsentFormValues) => {
    try {
      await grantConsent({
        contactId,
        consentType: values.consentType,
        source: values.source,
        consentBasis: values.consentBasis,
        phoneNumberE164: values.phoneNumberE164 && values.phoneNumberE164 !== ALL_PHONES ? values.phoneNumberE164 : undefined,
        notes: values.notes?.trim() || undefined,
        evidenceUrl: values.evidenceUrl?.trim() || undefined,
      }).unwrap();
      toast.success("Consent granted");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to grant consent"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant Consent</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="consentType" render={({ field }) => (
              <FormItem>
                <FormLabel>Consent Type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="CALL">Calling</SelectItem>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="CALL_AND_SMS">Calling & SMS</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="source" render={({ field }) => (
              <FormItem>
                <FormLabel>Source</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="MANUAL">Manual</SelectItem>
                    <SelectItem value="VERBAL">Verbal</SelectItem>
                    <SelectItem value="WEB_FORM">Web Form</SelectItem>
                    <SelectItem value="IMPORT">Import</SelectItem>
                    <SelectItem value="INTEGRATION">Integration</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="consentBasis" render={({ field }) => (
              <FormItem>
                <FormLabel>How consent was given</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={impliedBasis !== null}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="EXPRESS">Express: spoken, or agreed without a record kept</SelectItem>
                    <SelectItem value="WRITTEN">Written: a signed or web form, or a text or email you can show</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Only written consent lets the dialer play a recorded message to this number.
                </p>
              </FormItem>
            )} />

            <FormField control={form.control} name="phoneNumberE164" render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number (optional, applies to all if empty)</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue placeholder="All phone numbers" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value={ALL_PHONES}>All phone numbers</SelectItem>
                    {phones?.map((phone, index) => {
                      const number = phone.phoneNumberE164 || phone.phoneNumber || "";
                      return (
                        <SelectItem key={index} value={number}>{number}</SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="evidenceUrl" render={({ field }) => (
              <FormItem>
                <FormLabel>{consentBasis === "WRITTEN" ? "Evidence URL" : "Evidence URL (optional)"}</FormLabel>
                <FormControl><Input placeholder="https://..." {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes (optional)</FormLabel>
                <FormControl><Textarea rows={2} placeholder="How was consent obtained..." {...field} /></FormControl>
              </FormItem>
            )} />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Grant Consent
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default GrantConsentDialog;
